import React, { useState, useEffect } from 'react';
import './ContentForm.css';
import RichTextEditor from '../RichTextEditor/RichTextEditor';

// Image Gallery Popup Component
const ImageGalleryPopup = ({ isOpen, onClose, assets, onSelectImage }) => {
  if (!isOpen) return null;
  
  return (
    <div className="image-gallery-overlay">
      <div className="image-gallery-modal">
        <div className="image-gallery-header">
          <h4>Select an Image</h4>
          <button className="close-gallery-button" onClick={onClose}>×</button>
        </div>
        
        <div className="image-gallery-content">
          {assets && assets.length > 0 ? (
            <div className="image-grid">
              {assets.map((asset, index) => (
                <div 
                  key={index} 
                  className="image-item"
                  onClick={() => {
                    onSelectImage(asset.url);
                    onClose();
                  }}
                >
                  <img src={asset.url} alt={asset.name} />
                  <div className="image-name">{asset.name}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-images-message">
              <p>No images available in assets. Upload images in the Assets panel first.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Image Placeholder Component
const ImagePlaceholder = ({ label }) => {
  return (
    <div className="image-placeholder">
      <div className="placeholder-icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <circle cx="8.5" cy="8.5" r="1.5"></circle>
          <polyline points="21 15 16 10 5 21"></polyline>
        </svg>
      </div>
      <div className="placeholder-text">No Image Selected</div>
    </div>
  );
};

const ContentForm = ({ contentType, content, onSave, onCancel, onDelete, assets = [] }) => {
  const [formData, setFormData] = useState({});
  const [isNew, setIsNew] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(null);
  const [showGallery, setShowGallery] = useState(false);
  const [currentImageField, setCurrentImageField] = useState(null);

  useEffect(() => {
    if (content) {
      setFormData(content.data || {});
      setIsNew(false);
      setIsEditing(false); // Start in view mode for existing content
    } else {
      // Initialize empty form data based on content type fields
      const initialData = {};
      contentType.fields.forEach(field => {
        initialData[field.name] = '';
      });
      setFormData(initialData);
      setIsNew(true);
      setIsEditing(true); // Start in edit mode for new content
    }
    setError(null);
  }, [content, contentType]);

  const validateForm = () => {
    const errors = [];
    contentType.fields.forEach(field => {
      if (field.required) {
        const value = formData[field.name];
        if (value === undefined || value === '' || 
            (typeof value === 'string' && value.trim() === '')) {
          errors.push(`${field.label} is required`);
        }
      }
    });

    if (errors.length > 0) {
      setError(errors.join(', '));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSave(formData, content?._id);
      setIsEditing(false); // Switch to view mode after saving
      setError(null);
    } catch (error) {
      setError('Failed to save content. Please try again.');
    }
  };

  const handleChange = (fieldName, value) => {
    setFormData({
      ...formData,
      [fieldName]: value
    });
    setError(null);
  };

  const openImageGallery = (fieldName) => {
    setCurrentImageField(fieldName);
    setShowGallery(true);
  };

  const handleSelectImage = (imageUrl) => {
    if (currentImageField) {
      handleChange(currentImageField, imageUrl);
    }
  };

  // Render field value in view mode
  const renderFieldValue = (field) => {
    const { name, type, originalType } = field;
    const value = content.data[name];
    
    // Use originalType if available, otherwise use type
    const fieldType = originalType || type;
    
    switch (fieldType) {
      case 'boolean':
        return value ? 'Yes' : 'No';
        
      case 'richText':
        return <div className="rich-text-content" dangerouslySetInnerHTML={{ __html: value || '' }} />;
        
      case 'image':
      case 'cart_image':
        return value ? (
          <div className="view-image-container">
            <img src={value} alt={`${field.label || field.name}`} />
          </div>
        ) : (
          <ImagePlaceholder label={field.label || field.name} />
        );
        
      default:
        return value || '';
    }
  };

  const renderField = (field) => {
    // Use originalType if available, otherwise use type
    const fieldType = field.originalType || field.type;
    
    switch (fieldType) {
      case 'text':
      case 'cart_name':
        return (
          <input
            type="text"
            value={formData[field.name] || ''}
            onChange={(e) => handleChange(field.name, e.target.value)}
            placeholder={field.label}
            required={field.required}
          />
        );
      case 'number':
      case 'cart_price':
        return (
          <input
            type="number"
            value={formData[field.name] || ''}
            onChange={(e) => handleChange(field.name, e.target.value)}
            placeholder={field.label}
            required={field.required}
            step="0.01"
          />
        );
      case 'textarea':
        return (
          <textarea
            value={formData[field.name] || ''}
            onChange={(e) => handleChange(field.name, e.target.value)}
            placeholder={field.label}
            required={field.required}
            rows={5}
          />
        );
      case 'richText':
        return (
          <RichTextEditor
            value={formData[field.name] || ''}
            onChange={(htmlContent) => handleChange(field.name, htmlContent)}
            className="cms-rich-text-editor"
            placeholder={`Enter ${field.label || field.name} content...`}
            onBlur={() => validateForm()}
          />
        );
      case 'date':
        return (
          <input
            type="date"
            value={formData[field.name] || ''}
            onChange={(e) => handleChange(field.name, e.target.value)}
            required={field.required}
          />
        );
      case 'boolean':
        return (
          <div className="boolean-field">
            <input
              type="checkbox"
              checked={formData[field.name] || false}
              onChange={(e) => handleChange(field.name, e.target.checked)}
              id={`field-${field.name}`}
            />
            <label htmlFor={`field-${field.name}`}>{field.label}</label>
          </div>
        );
      case 'image':
      case 'cart_image':
        return (
          <div className="image-field">
            <input
              type="hidden"
              value={formData[field.name] || ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
            />
            
            <div className="image-field-container">
              {formData[field.name] ? (
                <div className="image-preview-container">
                  <img 
                    src={formData[field.name]} 
                    alt="Preview" 
                    className="image-preview-img"
                  />
                  <div className="image-actions">
                    <button 
                      type="button" 
                      className="change-image-button"
                      onClick={() => openImageGallery(field.name)}
                    >
                      Change Image
                    </button>
                    <button 
                      type="button" 
                      className="remove-image-button"
                      onClick={() => handleChange(field.name, '')}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div className="image-placeholder-container">
                  <ImagePlaceholder label={field.label} />
                  <button 
                    type="button" 
                    className="select-asset-button"
                    onClick={() => openImageGallery(field.name)}
                  >
                    Select Image
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      default:
        return (
          <input
            type="text"
            value={formData[field.name] || ''}
            onChange={(e) => handleChange(field.name, e.target.value)}
            placeholder={field.label}
            required={field.required}
          />
        );
    }
  };

  if (!isEditing && content?._id) {
    return (
      <div className="content-view">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        <div className="content-fields">
          {contentType.fields.map(field => (
            <div key={field.name} className="field-view">
              <label>{field.label || field.name}</label>
              <div className="field-value">
                {renderFieldValue(field)}
              </div>
            </div>
          ))}
        </div>
        <div className="content-actions">
          <button 
            type="button" 
            className="delete-button"
            onClick={() => {
              if (window.confirm('Are you sure you want to delete this content?')) {
                onDelete(content._id);
              }
            }}
          >
            Delete
          </button>
          <button 
            type="button" 
            className="edit-button"
            onClick={() => {
              setIsEditing(true);
              setError(null);
            }}
          >
            Edit
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="content-form-container">
      <ImageGalleryPopup 
        isOpen={showGallery}
        onClose={() => setShowGallery(false)}
        assets={assets}
        onSelectImage={handleSelectImage}
      />
      
      <form onSubmit={handleSubmit} className="content-form">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        
        <div className="form-fields">
          {contentType.fields.map((field) => (
            <div key={field.name} className="form-field">
              {renderField(field)}
            </div>
          ))}
        </div>
        
        <div className="form-actions">
          {!isNew && onDelete && (
            <button 
              type="button" 
              className="delete-content-button"
              onClick={() => onDelete(content._id)}
            >
              Delete
            </button>
          )}
          <button 
            type="button" 
            className="cancel-content-button"
            onClick={() => {
              if (content?._id) {
                setIsEditing(false);
              } else {
                onCancel();
              }
              setError(null);
            }}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="save-content-button"
          >
            {isNew ? 'Create' : 'Update'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContentForm; 